import type { Ssm5FreezeFramesDocumentFormat } from '@packages/domain/ssm5';

import { useAbortController } from '@packages/core/http';
import { getLogger } from '@packages/core/logging';
import { GetFreezeFramesDocument } from '@packages/domain/ssm5';
import { container } from '@web/services/container';
import { downloadBlob } from '@web/utils/download-file';
import { useCallback, useState } from 'react';

import type { Ssm5FreezeFramesTarget } from '../helpers/ssm5-paths';

import { freezeFramesDocumentName } from '../helpers/freeze-frame-document';
import { freezeFrameSearchText } from '../helpers/freeze-frame-search';

const [$getFreezeFramesDocument] = container.resolveOperations(GetFreezeFramesDocument);
const logger = getLogger('ssm5');

/** The service document download and its notice. */
export type Ssm5FreezeFramesDocumentDownload = {
    /** Hide the notice of the last download. */
    dismiss: () => void;
    /** Ask the service for the document in a format and save it. */
    download: (format: Ssm5FreezeFramesDocumentFormat) => void;
    /** Whether the last download was saved and its notice is still up. */
    isDownloaded: boolean;
};

/**
 * Download and save the service document, aborting running downloads on unmount.
 *
 * @param cloudConnectorId - the Cloud Connector ID of the session
 * @param target - the DTC whose document to download
 * @param query - the current PID name fragment
 * @returns the download actions and notice state
 */
export function useSsm5FreezeFramesDocument(
    cloudConnectorId: string,
    { dtcCode, module }: Ssm5FreezeFramesTarget,
    query: string
): Ssm5FreezeFramesDocumentDownload {
    const { abortSignalRef } = useAbortController();
    const [isDownloaded, setIsDownloaded] = useState(false);

    const dismiss = useCallback(() => setIsDownloaded(false), []);
    const download = useCallback(
        (format: Ssm5FreezeFramesDocumentFormat) => {
            const signal = abortSignalRef.current;

            setIsDownloaded(false);
            $getFreezeFramesDocument({
                cloudConnectorId,
                dtcCode,
                format,
                module,
                search: freezeFrameSearchText(query),
                signal,
            })
                .then((file) => {
                    if (!signal.aborted) {
                        downloadBlob(file, freezeFramesDocumentName(dtcCode, format));
                        setIsDownloaded(true);
                    }
                })
                .catch((error: unknown) => {
                    if (!signal.aborted) {
                        logger.error(
                            `The SSM5 freeze frame document download of [${dtcCode}] as [${format}] through [${cloudConnectorId}] failed`,
                            { cloudConnectorId, dtcCode, error, format, module }
                        );
                    }
                });
        },
        [abortSignalRef, cloudConnectorId, dtcCode, module, query]
    );

    return { dismiss, download, isDownloaded };
}
